import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useBranch } from "../contexts/BranchContext";
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  User,
  Clock,
  CreditCard,
  Bell,
  Save,
  AlertCircle,
} from "lucide-react";
import toast from "react-hot-toast";

interface BranchSettings {
  name: string;
  address: string;
  phone: string;
  email: string;
  manager: string;
  operatingHours: {
    start: string;
    end: string;
  };
  feeReminders: boolean;
  attendanceAlerts: boolean;
  autoBackup: boolean;
}

const BranchSettings: React.FC = () => {
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  const [settings, setSettings] = useState<BranchSettings>({
    name: "",
    address: "",
    phone: "",
    email: "",
    manager: "",
    operatingHours: {
      start: "09:00",
      end: "17:00",
    },
    feeReminders: true,
    attendanceAlerts: true,
    autoBackup: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedBranch) {
      const addressString = selectedBranch.address
        ? `${selectedBranch.address.street || ""}, ${
            selectedBranch.address.city || ""
          }, ${selectedBranch.address.state || ""} ${
            selectedBranch.address.zipCode || ""
          }, ${selectedBranch.address.country || ""}`
            .replace(/,\s*,/g, ",")
            .trim()
        : "";

      setSettings({
        name: selectedBranch.name || "",
        address: addressString,
        phone: selectedBranch.contactInfo?.phone || "",
        email: selectedBranch.contactInfo?.email || "",
        manager: selectedBranch.adminUser || "",
        operatingHours: {
          start: "09:00",
          end: "17:00",
        },
        feeReminders: true,
        attendanceAlerts: true,
        autoBackup: false,
      });
    }
  }, [selectedBranch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // TODO: Implement API call to update branch settings
      // await updateBranchSettings(selectedBranch?._id, settings);
      toast.success("Branch settings updated successfully");
    } catch (error: any) {
      console.error("Error updating branch settings:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update branch settings";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof BranchSettings, value: any) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOperatingHoursChange = (type: "start" | "end", value: string) => {
    setSettings((prev) => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [type]: value,
      },
    }));
  };

  if (!selectedBranch) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No branch data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Building2 className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Branch Settings
              </h1>
              <p className="text-gray-600">
                Manage your branch configuration and preferences
              </p>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            Branch ID: {selectedBranch._id}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Branch Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Building2 className="w-5 h-5 mr-2" />
              Branch Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Branch Name
                </label>
                <input
                  type="text"
                  value={settings.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manager
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={settings.manager}
                    onChange={(e) =>
                      handleInputChange("manager", e.target.value)
                    }
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Branch Manager Name"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <textarea
                    value={settings.address}
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                    rows={2}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Branch Address"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    value={settings.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Branch Phone"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={settings.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Branch Email"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Operating Hours */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              Operating Hours
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opening Time
                </label>
                <input
                  type="time"
                  value={settings.operatingHours.start}
                  onChange={(e) =>
                    handleOperatingHoursChange("start", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Closing Time
                </label>
                <input
                  type="time"
                  value={settings.operatingHours.end}
                  onChange={(e) =>
                    handleOperatingHoursChange("end", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Notifications & Alerts */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Bell className="w-5 h-5 mr-2" />
              Notifications & Alerts
            </h2>
            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={settings.feeReminders}
                  onChange={(e) =>
                    handleInputChange("feeReminders", e.target.checked)
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="flex items-center space-x-2">
                  <CreditCard className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">
                    Send fee payment reminders
                  </span>
                </div>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={settings.attendanceAlerts}
                  onChange={(e) =>
                    handleInputChange("attendanceAlerts", e.target.checked)
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">
                    Send attendance alerts
                  </span>
                </div>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={settings.autoBackup}
                  onChange={(e) =>
                    handleInputChange("autoBackup", e.target.checked)
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="flex items-center space-x-2">
                  <Save className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">
                    Enable automatic data backup
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? "Saving..." : "Save Settings"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BranchSettings;
